import { useEffect, useRef, useState } from "react";
import type { SkillCategory, Specialisation, YearOfStudy } from "@intern-seek/shared";
import { SKILL_CATEGORIES, SPECIALISATION_SHORT_LABELS, YEAR_OF_STUDY_LABELS } from "@intern-seek/shared";

export function PageHeading({ title, description }: { title: string; description?: string }) {
  return (
    <div className="mb-6">
      <h1 className="text-2xl font-semibold text-heading">{title}</h1>
      {description && <p className="mt-1 text-uoa-grey">{description}</p>}
    </div>
  );
}

export function Alert({ tone = "info", children }: { tone?: "info" | "error" | "success"; children: React.ReactNode }) {
  const tones = {
    info: "border-uoa-blue/30 bg-uoa-blue/5 text-heading",
    error: "border-red-300 bg-red-50 text-red-800",
    success: "border-green-300 bg-green-50 text-green-800",
  };
  return (
    <div role={tone === "error" ? "alert" : "status"} className={`mb-4 rounded-md border px-4 py-3 text-sm ${tones[tone]}`}>
      {children}
    </div>
  );
}

export function StatusPill({ status }: { status: string }) {
  const tone =
    status === "PUBLISHED" || status === "APPROVED" || status === "SUBMITTED" || status === "ACCEPTED"
      ? "bg-green-100 text-green-800"
      : status.startsWith("PENDING") || status === "REQUESTED" || status === "UNDER_REVIEW"
        ? "bg-amber-100 text-amber-800"
        : status === "REJECTED" || status === "DECLINED" || status === "CLOSED"
          ? "bg-red-100 text-red-800"
          : "bg-gray-100 text-gray-700";
  return <span className={`inline-block pill-shape px-2 py-0.5 text-xs font-medium ${tone}`}>{status.replace(/_/g, " ").toLowerCase()}</span>;
}

export function Spinner({ label = "Loading" }: { label?: string }) {
  return <p role="status" className="py-12 text-center text-uoa-grey">{label}...</p>;
}

export function Empty({ children }: { children: React.ReactNode }) {
  return <p className="rounded-md border border-dashed border-line px-4 py-10 text-center text-uoa-grey">{children}</p>;
}

/**
 * One shared colour for every skill, not one per skill. Nine distinct hues
 * said nothing -- the tag text already names the skill, so the colour carried
 * no information and four of the nine failed contrast as white-on-saturated
 * (amber 3.19:1, green 3.22:1, orange 3.59:1, teal 3.66:1). Faded green at
 * green-800 on green-100 measures 6.45:1.
 *
 * Colour comes back when skills get grouped -- software, hardware and so on --
 * because then the hue would stand for the group rather than for the
 * individual tag. One style per group, not per skill.
 *
 * Light-only on purpose, like the year pills: the fill and its text travel
 * together, so the pill stays legible on a dark page without a dark variant.
 */
const SKILL_PILL = {
  on: "border-green-700 bg-green-100 text-green-800",
  off: "border-green-700 bg-surface text-uoa-grey hover:bg-uoa-sand",
};

/**
 * The green every pill border uses. Not a lighter, prettier green on purpose:
 * an unselected filter pill has no fill, so its border is the only thing
 * marking the control, which puts it under 1.4.11's 3:1 non-text contrast bar.
 * green-700 is the only shade that clears it on all four grounds the pill can
 * sit on -- light and dark, card and page (4.94, 4.42, 3.16, 3.61). green-300
 * measured 1.40:1 on a white card, which is no border at all.
 */
const PILL_BORDER = "border-green-700";

/**
 * Filter-panel pill for a field with no palette of its own. Separate from
 * DEFAULT_PILL_STYLE because that one is also the EOI form's pill, which is
 * not part of the filter panel and keeps its blue outline.
 */
export const FILTER_PILL = {
  on: `${PILL_BORDER} bg-uoa-blue text-white`,
  off: `${PILL_BORDER} bg-surface text-uoa-ink hover:bg-uoa-sand`,
};

/**
 * Specialisation is told apart by its icon, not by colour, so the pill is
 * pure black and white: it inverts the page, dark pill on the light theme and
 * light pill on the dark one.
 *
 * uoa-ink and uoa-sand are the body text and page background, and both already
 * flip with the theme, so using them against each other inverts for free --
 * there is no dark: variant here to keep in sync. Every colour is a whole
 * token for that same reason: an opacity modifier like uoa-ink/10 compiles to
 * a color-mix against the light hex, which does not flip, so that hover would
 * have vanished on the dark theme.
 *
 * The three entries are identical by design. The per-value shape is what
 * PillMultiFilter's `styles` prop takes, and YEAR_STYLES below does still
 * vary per value -- year stays the one colour-coded field in the table.
 */
const SPECIALISATION_PILL = {
  on: "border-uoa-ink bg-uoa-ink text-uoa-sand",
  off: "border-green-700 bg-surface text-uoa-ink hover:bg-uoa-sand",
};

export const SPECIALISATION_STYLES: Record<Specialisation, { on: string; off: string }> = {
  CSE: SPECIALISATION_PILL,
  SWE: SPECIALISATION_PILL,
  EEE: SPECIALISATION_PILL,
};

/**
 * A gradient, not a colour per value -- year of study is a sequence, not a
 * set of categories (see the comment on SegmentedControl). Deliberately
 * uneven steps, not a straight line up the Tailwind scale: Part 1 sits at
 * blue-100, almost white, and the 2->3 step is the biggest jump in the
 * sequence (300 -> 700) rather than an even middle step, so each part has a
 * shade distinct enough to recognise on sight rather than four similar
 * blues you have to compare side by side. Part 4 stays blue-950, about as
 * close to black-blue as the scale gets.
 */
export const YEAR_STYLES: Record<YearOfStudy, { on: string; off: string }> = {
  PART1: {
    on: "border-blue-200 bg-blue-100 text-blue-900",
    off: "border-green-700 bg-white text-blue-400 hover:bg-blue-50",
  },
  PART2: {
    on: "border-blue-400 bg-blue-300 text-blue-950",
    off: "border-green-700 bg-white text-blue-600 hover:bg-blue-50",
  },
  PART3: {
    on: "border-blue-700 bg-blue-700 text-white",
    off: "border-green-700 bg-white text-blue-800 hover:bg-blue-50",
  },
  PART4: {
    on: "border-blue-950 bg-blue-950 text-white",
    off: "border-green-700 bg-white text-blue-950 hover:bg-blue-50",
  },
};

/** Interactive pill bar: pick any number of core skills. */
export function SkillPicker({
  value,
  onChange,
  disabled,
  compact,
}: {
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  /** Smaller pills, for a dense context like a filter panel rather than the editing form. */
  compact?: boolean;
}) {
  const toggle = (category: SkillCategory) => {
    onChange(value.includes(category) ? value.filter((v) => v !== category) : [...value, category]);
  };
  const size = compact ? "px-2.5 py-1 text-xs" : "px-3 py-1.5 text-sm";
  const gap = compact ? "gap-1.5" : "gap-2";

  return (
    <div role="group" aria-label="Core skills" className={`flex flex-wrap ${gap}`}>
      {SKILL_CATEGORIES.map((category) => {
        const selected = value.includes(category);
        const style = SKILL_PILL;
        return (
          <button
            key={category}
            type="button"
            aria-pressed={selected}
            disabled={disabled}
            onClick={() => toggle(category)}
            className={`pill-shape border ${size} font-medium transition disabled:cursor-not-allowed disabled:opacity-50 ${
              selected ? style.on : style.off
            }`}
          >
            {category}
          </button>
        );
      })}
    </div>
  );
}

/** Non-editing version of the same tags, for showing a student's skills elsewhere. */
export function SkillTags({ skills, onToggle, activeSkills }: {
  skills: string[];
  /** Given, each tag becomes a filter control -- see Pill. */
  onToggle?: (skill: string) => void;
  activeSkills?: string[];
}) {
  if (skills.length === 0) return null;
  return (
    <ul className="flex flex-wrap gap-1.5">
      {skills.map((category) => (
        <li key={category}>
          <Pill
            label={category}
            className={SKILL_PILL.on}
            onClick={onToggle && (() => onToggle(category))}
            active={activeSkills?.includes(category)}
          />
        </li>
      ))}
    </ul>
  );
}

/** Fallback when a caller doesn't have a per-option palette to hand in. */
const DEFAULT_PILL_STYLE = {
  on: "border-uoa-blue bg-uoa-blue text-white",
  off: "border-uoa-blue-light bg-uoa-blue/5 text-heading hover:bg-uoa-blue/10 dark:bg-uoa-blue/15 dark:hover:bg-uoa-blue/25",
};

/**
 * Single-choice pill row. Flat single-accent by default -- only one option
 * can ever be active, unlike SkillPicker -- but takes a per-option
 * palette (SPECIALISATION_STYLES, say) when the options genuinely are
 * distinct categories rather than degrees of the same thing. Radio
 * semantics: clicking the selected pill again does nothing.
 */
export function PillRadioGroup<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  styles,
}: {
  options: { value: T; label: string }[];
  value: T | "";
  onChange: (next: T) => void;
  ariaLabel: string;
  styles?: Partial<Record<T, { on: string; off: string }>>;
}) {
  return (
    <div role="radiogroup" aria-label={ariaLabel} className="flex flex-wrap gap-2">
      {options.map((opt) => {
        const selected = value === opt.value;
        const style = styles?.[opt.value] ?? DEFAULT_PILL_STYLE;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(opt.value)}
            className={`pill-shape border px-4 py-1.5 text-sm font-medium transition ${selected ? style.on : style.off}`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Single-choice, joined into one bar rather than floating pills -- for a
 * short ordered sequence (year of study), where separate pills would read
 * as unrelated options rather than steps in a progression. `styles` is
 * where that progression shows: pass a gradient (YEAR_STYLES) rather than
 * one colour per segment, or omit it for the flat default.
 */
export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  styles,
}: {
  options: { value: T; label: string }[];
  value: T | "";
  onChange: (next: T) => void;
  ariaLabel: string;
  styles?: Partial<Record<T, { on: string; off: string }>>;
}) {
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className="inline-flex max-w-full overflow-x-auto rounded-md border border-uoa-blue-light"
    >
      {options.map((opt, i) => {
        const selected = value === opt.value;
        const style = styles?.[opt.value] ?? DEFAULT_PILL_STYLE;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(opt.value)}
            className={`whitespace-nowrap px-4 py-2 text-sm font-medium transition ${i > 0 ? "border-l border-uoa-blue-light" : ""} ${
              selected ? style.on : style.off
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Outlined box whose label sits on the border line itself, top-left, like a
 * <fieldset>/<legend> -- for grouping a filter bar into named fields
 * without a filled background per field. The label's background has to
 * match whatever sits behind the panel (bg-surface, same token the card
 * it sits on already uses) or the "cut" won't read.
 */
export function FieldPanel({ label, full, children }: { label: string; full?: boolean; children: React.ReactNode }) {
  return (
    <div className={`relative rounded-lg border border-line p-3 pt-4 ${full ? "sm:col-span-full" : ""}`}>
      <span className="absolute -top-2.5 left-3 bg-surface px-1.5 text-xs font-semibold uppercase tracking-wide text-uoa-grey">
        {label}
      </span>
      {children}
    </div>
  );
}

/**
 * One pill, read-only by default and a filter control once the caller hands
 * in onClick -- identical box either way, so a clickable pill in a table row
 * still lines up with a plain one elsewhere. Clicking an already-active
 * value removes it again; the filter panel is what displays the current
 * selection, so there is no second active state drawn on the pill itself.
 */
function Pill({ label, className, onClick, active }: {
  label: string;
  className: string;
  onClick?: () => void;
  active?: boolean;
}) {
  const box = `inline-block whitespace-nowrap pill-shape border px-2.5 py-0.5 text-xs font-medium ${className}`;
  if (!onClick) return <span className={box}>{label}</span>;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      title={active ? `Remove the ${label} filter` : `Filter by ${label}`}
      className={`${box} cursor-pointer transition hover:opacity-70`}
    >
      {label}
    </button>
  );
}

/** Plain single-accent badge, for a field with no dedicated palette of its own. */
export function Badge({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-block whitespace-nowrap pill-shape border border-uoa-blue-light bg-uoa-blue/10 px-2.5 py-0.5 text-xs font-medium text-heading">
      {children}
    </span>
  );
}

/** Specialisation badge, coloured per SPECIALISATION_STYLES. */
export function SpecialisationBadge({ value, onClick, active }: {
  value: Specialisation;
  onClick?: () => void;
  active?: boolean;
}) {
  return (
    <Pill label={SPECIALISATION_SHORT_LABELS[value]} className={SPECIALISATION_STYLES[value].on} onClick={onClick} active={active} />
  );
}

/** Year-of-study badge, coloured per YEAR_STYLES' gradient. */
export function YearBadge({ value, onClick, active }: {
  value: YearOfStudy;
  onClick?: () => void;
  active?: boolean;
}) {
  return (
    <Pill label={YEAR_OF_STUDY_LABELS[value]} className={YEAR_STYLES[value].on} onClick={onClick} active={active} />
  );
}

/**
 * Multi-select toggle in the single-accent style, for filtering by a field
 * that is itself single-choice per record (specialisation, year) -- you
 * can't have two specialisations, but a filter can still mean "either of
 * these two". Same visual family as PillRadioGroup, toggle semantics
 * instead of radio.
 */
export function PillMultiFilter<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  styles,
  compact,
}: {
  options: { value: T; label: string }[];
  value: T[];
  onChange: (next: T[]) => void;
  ariaLabel: string;
  styles?: Partial<Record<T, { on: string; off: string }>>;
  /** Smaller pills, for a dense context like a filter panel. */
  compact?: boolean;
}) {
  const toggle = (v: T) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  const size = compact ? "px-2.5 py-1 text-xs" : "px-3 py-1.5 text-sm";
  const gap = compact ? "gap-1.5" : "gap-2";
  return (
    <div role="group" aria-label={ariaLabel} className={`flex flex-wrap ${gap}`}>
      {options.map((opt) => {
        const selected = value.includes(opt.value);
        const style = styles?.[opt.value] ?? DEFAULT_PILL_STYLE;
        return (
          <button
            key={opt.value}
            type="button"
            aria-pressed={selected}
            onClick={() => toggle(opt.value)}
            className={`pill-shape border ${size} font-medium transition ${selected ? style.on : style.off}`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Comma-separated entry: type text and a comma commits it to the list below
 * (typing or pasting several at once both work, since a paste just arrives
 * as one onChange with commas in it). The per-line button is the only way to
 * remove an entry: backspace on an empty draft dropping the last one is a
 * mail-client chip convention, and it reads as a glitch now that the entries
 * are a plain list rather than pills you can see yourself backing into.
 *
 * Deliberately a plain list rather than pills: these fields are free text, so
 * no two students write the same thing and there is no shared vocabulary for
 * a pill to stand for -- which is also why the company-facing directory
 * searches them instead of offering a picker. Pills are reserved for the
 * fields that really are a fixed set (skills, specialisation, year).
 */
export function TagInput({
  value,
  onChange,
  placeholder,
  ariaLabel,
  maxLength = 40,
}: {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  ariaLabel: string;
  maxLength?: number;
}) {
  const [draft, setDraft] = useState("");

  const commit = (raw: string) => {
    const parts = raw.split(",").map((s) => s.trim()).filter(Boolean);
    if (parts.length === 0) return;
    const next = [...value];
    for (const part of parts) if (!next.includes(part)) next.push(part);
    onChange(next);
  };

  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className="rounded-md border border-line bg-surface focus-within:border-uoa-blue"
    >
      <input
        type="text"
        value={draft}
        maxLength={maxLength}
        onChange={(e) => {
          const next = e.target.value;
          if (next.includes(",")) {
            commit(next);
            setDraft("");
          } else {
            setDraft(next);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commit(draft);
            setDraft("");
          }
        }}
        onBlur={() => {
          if (draft.trim()) {
            commit(draft);
            setDraft("");
          }
        }}
        placeholder={placeholder}
        className="w-full border-0 bg-transparent px-3 py-2 text-sm placeholder:text-uoa-grey outline-none"
      />
      {value.length > 0 && (
        <ul className="border-t border-line">
          {value.map((tag) => (
            <li
              key={tag}
              className="flex items-start justify-between gap-2 border-b border-line px-3 py-1.5 text-sm last:border-b-0"
            >
              <span className="min-w-0 break-words">{tag}</span>
              <button
                type="button"
                onClick={() => onChange(value.filter((t) => t !== tag))}
                aria-label={`Remove ${tag}`}
                className="shrink-0 px-1 text-base leading-tight text-uoa-grey transition hover:text-red-700"
              >
                &times;
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Modal built on the native <dialog>, so Esc, focus trapping and the backdrop
 * come from the platform rather than being reimplemented. showModal() has to
 * be called imperatively -- the `open` attribute alone renders a non-modal
 * dialog with no backdrop and no focus trap.
 */
export function Modal({ open, onClose, title, children }: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      onClose={onClose}
      // A click landing on the dialog itself rather than its content is a
      // click on the backdrop, since the padding belongs to the inner box.
      onClick={(e) => { if (e.target === ref.current) onClose(); }}
      className="m-auto w-[min(42rem,calc(100vw-2rem))] rounded-lg border border-line bg-surface p-0 text-uoa-ink backdrop:bg-black/50"
    >
      <div className="flex items-start justify-between gap-4 border-b border-line px-5 py-3">
        <h2 className="text-lg font-semibold text-heading">{title}</h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="shrink-0 px-1 text-xl leading-none text-uoa-grey transition hover:text-uoa-ink"
        >
          &times;
        </button>
      </div>
      <div className="max-h-[70vh] overflow-y-auto px-5 py-4">{children}</div>
    </dialog>
  );
}
